// A list that hangs under an input (search results, a combobox) without being cut off by
// whatever scrolls around it: in a dialog the body scrolls and would clip an `absolute` list.
// From 640px it floats in its own layer on <body>, tied to the anchor's box, and opens upward
// when there is more room above. On a phone it stays in the flow under the input, so it pushes
// the sheet's other fields down instead of covering them over the keyboard.
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx.ts'

/**
 * Where a floating list is put: the open dialog's panel (a screen reader treats everything
 * outside an `aria-modal` dialog as gone), else <body>. The panel does not clip; only its
 * scrolling body does, and the list is not inside that.
 */
export const FloatHost = createContext<HTMLElement | null>(null)

const GAP = 4
const EDGE = 8

const narrowQuery = typeof window === 'undefined' ? null : window.matchMedia('(max-width: 639px)')

function useNarrow(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      narrowQuery?.addEventListener('change', onChange)
      return () => narrowQuery?.removeEventListener('change', onChange)
    },
    () => narrowQuery?.matches ?? false,
  )
}

/** Where the list goes: below the anchor unless it fits better above. Pure, for tests. */
export function anchoredPlace(
  anchor: { top: number; bottom: number },
  viewportHeight: number,
  wanted: number,
): { above: boolean; maxHeight: number } {
  const below = viewportHeight - anchor.bottom - GAP - EDGE
  const above = anchor.top - GAP - EDGE
  const up = below < wanted && above > below
  return { above: up, maxHeight: Math.max(0, Math.min(wanted, up ? above : below)) }
}

export function AnchoredList({
  anchor,
  children,
  className,
  maxHeight = 320,
}: {
  /** The box the list hangs from (the input's wrapper); the list takes its width. */
  anchor: RefObject<HTMLElement | null>
  children: ReactNode
  className?: string
  /** The tallest the list may be; it scrolls inside beyond that. */
  maxHeight?: number
}) {
  const narrow = useNarrow()
  const host = useContext(FloatHost)
  const panel = useRef<HTMLDivElement>(null)

  // The position is written straight onto the element: it follows every scroll and resize,
  // and a re-render for each would be wasted work.
  useLayoutEffect(() => {
    if (narrow) return
    let frame = 0
    const place = () => {
      const box = anchor.current?.getBoundingClientRect()
      const el = panel.current
      if (!box || !el) return
      const { above, maxHeight: h } = anchoredPlace(box, window.innerHeight, maxHeight)
      el.style.left = `${box.left}px`
      el.style.width = `${box.width}px`
      el.style.maxHeight = `${h}px`
      el.style.top = above ? '' : `${box.bottom + GAP}px`
      el.style.bottom = above ? `${window.innerHeight - box.top + GAP}px` : ''
    }
    const later = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }
    place()
    window.addEventListener('scroll', later, true)
    window.addEventListener('resize', later)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', later, true)
      window.removeEventListener('resize', later)
    }
  }, [anchor, narrow, maxHeight])

  const look =
    'overflow-y-auto overscroll-contain rounded-xl border border-border bg-raised p-1 shadow-card-hover'
  if (narrow)
    return (
      // w-0 min-w-full: as wide as the input, and a long option never widens the page.
      <div className={cx('mt-1 w-0 min-w-full', look, className)} style={{ maxHeight }}>
        {children}
      </div>
    )
  // A portal still passes React events up through the dialog it was rendered in, so a click
  // in the list is never taken for a click on the dialog's backdrop either way.
  return createPortal(
    <div ref={panel} data-anchored="" className={cx('fixed z-[60]', look, className)}>
      {children}
    </div>,
    host ?? document.body,
  )
}
